import { useState, useEffect, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { SvgClose } from "ui/svgs";

type propsType = {
  onPlaceSelect: (info: {
    address: string;
    latitude: number;
    longitude: number;
    zip: string;
    city: string;
    state: string;
  }) => void;
  placeholder?: string;
  inputClassName?: string;
  initInputText?: string;
};
const AddressAutocomplete = ({
  onPlaceSelect,
  placeholder = "Please enter an address",
  inputClassName = "w-full px-4 py-4",
  initInputText = "",
}: propsType) => {
  const [inputValue, setInputValue] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const placesLibrary = useMapsLibrary("places");
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const sessionToken = useRef(null);
  const debounceTimeout = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    setInputValue(initInputText);
  }, [initInputText]);

  useEffect(() => {
    if (placesLibrary) {
      autocompleteService.current = new placesLibrary.AutocompleteService();
      sessionToken.current = new placesLibrary.AutocompleteSessionToken();

      // 创建一个隐藏的地图元素，用于 PlacesService
      if (!mapRef.current) {
        mapRef.current = document.createElement("div");
        mapRef.current.style.display = "none";
        document.body.appendChild(mapRef.current);
        const map = new window.google.maps.Map(mapRef.current);
        placesService.current = new placesLibrary.PlacesService(map);
      }
    }

    return () => {
      if (mapRef.current && mapRef.current.parentNode) {
        mapRef.current.parentNode.removeChild(mapRef.current);
      }
    };
  }, [placesLibrary]);

  const handleInputChange = (e) => {
    e.preventDefault();
    const value = e.target.value;
    setInputValue(value);

    // 清除之前的定时器
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (!value.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    // 设置新的定时器，延迟500毫秒
    debounceTimeout.current = setTimeout(() => {
      fetchPredictions(value);
    }, 500);
  };

  // get place predictions
  const fetchPredictions = async (input) => {
    if (!autocompleteService.current) return;

    setIsLoading(true);

    try {
      const request = {
        input,
        sessionToken: sessionToken.current,
        //componentRestrictions: { country: "cn" }, // 可以根据需要更改国家限制
      };

      const { predictions } =
        await autocompleteService.current.getPlacePredictions(request);

      setPredictions(predictions || []);
      setShowDropdown(predictions && predictions.length > 0);
    } catch (error) {
      console.error("获取地址预测失败:", error);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getPlaceDetails = (placeId) => {
    return new Promise((resolve, reject) => {
      if (!placesService.current) {
        reject(new Error("Places service not initialized"));
        return;
      }

      const request = {
        placeId: placeId,
        fields: [
          "address_components",
          "formatted_address",
          "geometry",
          "place_id",
        ],
      };

      placesService.current.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          reject(new Error(`获取地点详情失败: ${status}`));
        }
      });
    });
  };

  // 选择地址
  const handleSelectPlace = async (prediction) => {
    setInputValue(prediction.description);
    setPredictions([]);
    setShowDropdown(false);
    setIsLoading(true);

    try {
      const res = await getPlaceDetails(prediction.place_id);
      const placeDetails = res as any;
      const latitude = placeDetails?.geometry?.location?.lat();
      const longitude = placeDetails?.geometry?.location?.lng();
      console.log("placeDetails", placeDetails);
      let city = "",
        state = "",
        zip = "";
      if (placeDetails.address_components) {
        for (const component of placeDetails.address_components) {
          const types = component.types;

          if (
            types.includes("locality") ||
            types.includes("sublocality") ||
            types.includes("administrative_area_level_2")
          ) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            state = component.short_name;
          } else if (types.includes("postal_code")) {
            zip = component.long_name;
          }
        }
      }
      onPlaceSelect?.({
        latitude,
        longitude,
        city,
        state,
        zip,
        address: prediction.description,
      });
    } catch (error) {
      console.error("fail to get site detail:", error);
    } finally {
      setIsLoading(false);
    }

    // create new session token
    if (placesLibrary) {
      sessionToken.current = new placesLibrary.AutocompleteSessionToken();
    }
  };

  // click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={inputClassName}
          onFocus={() =>
            inputValue && predictions.length > 0 && setShowDropdown(true)
          }
        />
        <button
          onClick={(e) => {
            e.preventDefault();
            setInputValue("");
          }}
          className="absolute right-6 top-1/2 -translate-y-1/2"
        >
          <SvgClose
            width="13"
            className="fill-neutral-600 hover:fill-neutral-500"
          />
        </button>
        {isLoading && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {predictions.map((prediction) => (
            <li
              key={prediction.place_id}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              onClick={() => handleSelectPlace(prediction)}
            >
              <div className="font-medium">
                {prediction.structured_formatting?.main_text ||
                  prediction.description}
              </div>
              {prediction.structured_formatting?.secondary_text && (
                <div className="text-sm text-gray-500">
                  {prediction.structured_formatting.secondary_text}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
